import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Date helpers ───────────────────────────────────────────────────
const parseDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v === "number") { const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; }
    if (typeof v !== "string") return null;
    let s = v.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [yy, mm, dd] = s.split("-").map(Number);
        return new Date(yy, mm - 1, dd, 0, 0, 0, 0);
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
    if (s.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const toLocalYMD = (v) => {
    const d = parseDate(v);
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const prettyLabel = (ymd) => {
    const [yy, mm, dd] = ymd.split("-").map(Number);
    return new Date(yy, mm - 1, dd).toLocaleDateString(undefined, { month: "short", day: "2-digit" });
};

const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const getLastConvDateValue = (row) =>
    row?.LastConvDate || row?.LastConvDateTime || row?.LastConvDateUtc ||
    row?.LastConvDateString || row?.lastConvDate || row?.lastconvdate;

// ── Component ──────────────────────────────────────────────────────
const TransfersLanguageStackedAreaChart = ({ rows, title = "Transfers" }) => {
    const { labels, english, spanish, chartableRows } = useMemo(() => {
        const map = new Map();
        let chartable = 0;
        (rows || []).forEach((r) => {
            const key = toLocalYMD(getLastConvDateValue(r));
            if (!key) return;
            chartable++;
            const cur = map.get(key) || { english: 0, spanish: 0 };
            cur.english += toNumber(r?.EnglishTotal);
            cur.spanish += toNumber(r?.Spanish);
            map.set(key, cur);
        });
        const keys = [...map.keys()].sort();
        return {
            labels:        keys,
            english:       keys.map(k => map.get(k)?.english ?? 0),
            spanish:       keys.map(k => map.get(k)?.spanish ?? 0),
            chartableRows: chartable,
        };
    }, [rows]);

    if (!rows?.length) return null;

    if (!labels.length) {
        return (
            <Paper elevation={0} sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px", mb: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {rows.length} rows loaded but no dates could be parsed.
                </Typography>
            </Paper>
        );
    }

    const data = {
        labels: labels.map(prettyLabel),
        datasets: [
            {
                label: "Spanish",
                data: spanish,
                backgroundColor: "#A6CAEC",
                fill: true, stack: "lang",
                borderWidth: 0, pointRadius: 0, pointHoverRadius: 0, tension: 0.35,
            },
            {
                label: "English",
                data: english,
                backgroundColor: "#DCEAF7",
                fill: true, stack: "lang",
                borderWidth: 0, pointRadius: 0, pointHoverRadius: 0, tension: 0.35,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: { position: "top", labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } } },
            tooltip: { enabled: true },
        },
        elements: { line: { borderWidth: 0 }, point: { radius: 0 } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.04)" } },
        },
    };

    return (
        <Paper elevation={0} sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px", mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                {title}
            </Typography>
            <Box sx={{ height: 260 }}>
                <Line data={data} options={options} />
            </Box>
        </Paper>
    );
};

export default TransfersLanguageStackedAreaChart;